import { PublicClientApplication, type Configuration, LogLevel } from '@azure/msal-browser';

/**
 * MSAL configuration for the AES I.T. Helpdesk SPA.
 *
 * Authority is single-tenant — only aesintl.com users can sign in. The redirect
 * URIs we register in Entra (http://localhost:5173/auth/callback and
 * https://helpdesk.aesintl.com/auth/callback) must be reachable for sign-in to
 * complete.
 *
 * sessionStorage > localStorage: tokens are wiped when the tab closes. This
 * matters less than it used to (refresh tokens are now short-lived for SPAs)
 * but it's still the right default for an internal app behind SSO.
 */
const config: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_ENTRA_TENANT_ID}`,
    redirectUri: `${window.location.origin}/auth/callback`,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      piiLoggingEnabled: false,
      loggerCallback(level, message, containsPii) {
        if (containsPii) return;
         
        if (level === LogLevel.Error) console.error('[msal]', message);
        else if (level === LogLevel.Warning) console.warn('[msal]', message);
      }
    }
  }
};

/** Scopes for sign-in (just identity). */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email']
};

/** Scopes for calling our own API. Cached separately by MSAL per scope set. */
export const apiRequest = {
  scopes: [import.meta.env.VITE_ENTRA_API_SCOPE]
};

export const msalInstance = new PublicClientApplication(config);
